import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { faKey } from '@fortawesome/free-solid-svg-icons';
import { customerAuthService } from '@/services/customer-auth.service';
import { useCustomerAuthStore } from '@/stores/customer-auth.store';
import { CUSTOMER_PORTAL_ROUTES } from '@/routes/customer-portal-routes';
import { CustomerPageContainer } from '@/components/customer-portal/CustomerPageContainer';
import { CustomerPageHeader } from '@/components/customer-portal/CustomerPageHeader';
import { CustomerSectionCard } from '@/components/customer-portal/CustomerSectionCard';
import { CustomerStatusBanner } from '@/components/customer-portal/CustomerStatusBanner';
import { CustomerButton } from '@/components/customer-portal/CustomerButton';
import {
  CustomerPasswordField,
  type CustomerPasswordRule,
} from '@/components/customer-portal/CustomerPasswordField';

/**
 * User Profile P2 — change own password (and the forced first change, S-11).
 *
 * The backend policy-checks the new password, clears `mustChangePassword`,
 * bumps `tokenVersion` and revokes EVERY refresh family — this session's too.
 * So on success the page signs the contact out deliberately and lands on the
 * login page with a notice (Owner decision P2-OD-3, the platform 12.8 pattern),
 * rather than letting the next request bounce with "Session expired".
 *
 * Secrets never leave component state: nothing is written to storage, and the
 * fields are cleared the moment the request settles.
 */

/**
 * Mirrors the ONE customer password policy (`validateCustomerPasswordPolicy`)
 * exactly — the same list the activation page renders. Weaker would let the
 * user submit something the server rejects; stricter would refuse passwords
 * the server accepts. Server violations are always shown verbatim.
 */
const PASSWORD_RULES: CustomerPasswordRule[] = [
  { label: 'At least 8 characters', test: (v) => v.length >= 8 },
  { label: 'One uppercase letter', test: (v) => /[A-Z]/.test(v) },
  { label: 'One lowercase letter', test: (v) => /[a-z]/.test(v) },
  { label: 'One number', test: (v) => /[0-9]/.test(v) },
  { label: 'One special character', test: (v) => /[^A-Za-z0-9]/.test(v) },
];

interface ApiErrorBody {
  error?: { code?: string; message?: string; violations?: string[] };
}

function errorBody(err: unknown): ApiErrorBody['error'] | undefined {
  return axios.isAxiosError(err)
    ? (err.response?.data as ApiErrorBody | undefined)?.error
    : undefined;
}

export function CustomerChangePasswordPage() {
  const navigate = useNavigate();
  const user = useCustomerAuthStore((s) => s.user);
  const clearAuth = useCustomerAuthStore((s) => s.clearAuth);
  const forced = !!user?.mustChangePassword;

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const errs: Record<string, string> = {};
    if (!currentPassword) errs.currentPassword = 'Enter your current password';
    if (PASSWORD_RULES.some((r) => !r.test(newPassword))) {
      errs.newPassword = 'Your password does not meet all the requirements yet';
    }
    if (newPassword && newPassword === currentPassword) {
      errs.newPassword = 'Your new password must be different from your current password';
    }
    if (newPassword !== confirm) errs.confirm = 'Both passwords must match';
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }
    setFieldErrors({});
    setLoading(true);
    try {
      await customerAuthService.changePassword(currentPassword, newPassword);
      // Clear the secrets the moment they are no longer needed, then sign out
      // on purpose: the server has already ended this session with the others.
      setCurrentPassword('');
      setNewPassword('');
      setConfirm('');
      clearAuth();
      navigate(CUSTOMER_PORTAL_ROUTES.LOGIN, {
        replace: true,
        state: { notice: 'password-changed' },
      });
    } catch (err) {
      const body = errorBody(err);
      if (axios.isAxiosError(err) && !err.response) {
        setFormError('Unable to reach the server. Check your connection and try again.');
      } else if (body?.code === 'PASSWORD_POLICY_VIOLATION') {
        setFieldErrors({
          newPassword: body.violations?.length
            ? body.violations.join('. ')
            : 'That password does not meet the requirements.',
        });
      } else if (
        axios.isAxiosError(err) &&
        err.response?.status === 400 &&
        /current password/i.test(body?.message ?? '')
      ) {
        setFieldErrors({ currentPassword: 'That is not your current password.' });
      } else {
        setFormError(body?.message ?? 'We could not change your password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <CustomerPageContainer>
      <CustomerPageHeader
        title="Change password"
        subtitle={
          forced
            ? 'Set a new password to continue.'
            : 'Choose a new password for your portal account.'
        }
      />

      <div className="mt-4 max-w-lg space-y-4">
        <CustomerStatusBanner
          tone={forced ? 'info' : 'warning'}
          title={forced ? 'You must set a new password before continuing' : 'Before you continue'}
          message="Changing your password signs you out of every device, including this one. You will need to sign in again with your new password."
          data-testid="change-password-warning"
        />

        <CustomerSectionCard title="New password" icon={faKey}>
          {formError && (
            <div
              role="alert"
              className="customer-modal-in mb-4 rounded-lg border border-customer-warning/40 bg-customer-warning/10 px-3 py-2 text-sm text-customer-text"
            >
              {formError}
            </div>
          )}
          <form onSubmit={submit} noValidate className="space-y-5">
            <CustomerPasswordField
              id="cp-current"
              label="Current password"
              value={currentPassword}
              onChange={setCurrentPassword}
              autoComplete="current-password"
              disabled={loading}
              error={fieldErrors.currentPassword}
              autoFocus
            />
            <CustomerPasswordField
              id="cp-new"
              label="New password"
              value={newPassword}
              onChange={setNewPassword}
              autoComplete="new-password"
              disabled={loading}
              error={fieldErrors.newPassword}
              rules={PASSWORD_RULES}
              show={showNew}
              onToggleShow={() => setShowNew((v) => !v)}
            />
            <CustomerPasswordField
              id="cp-confirm"
              label="Confirm new password"
              value={confirm}
              onChange={setConfirm}
              autoComplete="new-password"
              disabled={loading}
              error={fieldErrors.confirm}
              show={showNew}
              onToggleShow={() => setShowNew((v) => !v)}
            />
            <div className="flex flex-col gap-2 sm:flex-row-reverse sm:justify-start">
              <CustomerButton type="submit" loading={loading} loadingLabel="Changing…">
                Change password and sign out
              </CustomerButton>
              {!forced && (
                <Link
                  to={CUSTOMER_PORTAL_ROUTES.ACCOUNT}
                  className="customer-pressable inline-flex min-h-11 items-center justify-center rounded-customer-sm border border-customer-border bg-customer-surface px-4 text-sm font-medium text-customer-text hover:bg-customer-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring"
                >
                  Cancel
                </Link>
              )}
            </div>
          </form>
        </CustomerSectionCard>
      </div>
    </CustomerPageContainer>
  );
}
